import { Link } from "react-router-dom";
import {
  Home as HomeIcon, Zap, Thermometer, LayoutGrid, Check, ArrowRight,
  Fan, Snowflake, Wind, Boxes,
} from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import FAQ from "@/components/FAQ";
import { Eyebrow, GradientText, PrimaryButton } from "@/components/Brand";
import { Reveal } from "@/lib/motion";

const WHAT_CARDS = [
  { icon: HomeIcon, title: "Whole Home", text: "Designed for multiple rooms." },
  { icon: Zap, title: "Electric", text: "Uses reverse-cycle heat-pump technology." },
  { icon: Thermometer, title: "Heating + Cooling", text: "One system for year-round comfort." },
  { icon: LayoutGrid, title: "Zoning", text: "Compatible systems can provide room or area control." },
];

const BENEFITS = [
  { title: "Whole-Home Comfort", text: "Condition multiple rooms from one integrated system." },
  { title: "Clean Interior", text: "Only discreet grilles or diffusers are visible within conditioned rooms." },
  { title: "Flexible Zoning", text: "Compatible systems can divide the property into separate comfort zones." },
  { title: "Reverse Cycle", text: "Heating and cooling from the same system." },
  { title: "Smart Control", text: "Selected systems support Wi-Fi, apps and advanced controllers." },
  { title: "Tailored Capacity", text: "System sizing can be designed around the individual property's layout and heat-load requirements." },
];

const DIAGRAM = [
  { icon: Fan, title: "Outdoor Unit", text: "Reverse-cycle condenser positioned outside the home." },
  { icon: Snowflake, title: "Indoor Fan Coil", text: "Concealed in the ceiling or under-floor space." },
  { icon: Wind, title: "Ductwork", text: "Insulated ducts distribute conditioned air." },
  { icon: Boxes, title: "Rooms / Zones", text: "Discreet outlets deliver comfort where you want it." },
];

const FAQS = [
  { q: "Can ducted air conditioning heat and cool?", a: "Yes. Reverse-cycle ducted systems can provide both refrigerated cooling and heating." },
  { q: "Can I control different rooms separately?", a: "Many ducted systems can be configured with zones, allowing selected rooms or areas to operate separately." },
  { q: "Where is the indoor unit installed?", a: "It is commonly installed within the ceiling cavity, although the configuration depends on the property." },
  { q: "What size ducted system do I need?", a: "Correct capacity depends on factors including floor area, insulation, windows, room orientation, ceiling height, occupancy and property layout." },
  { q: "Can ducted systems have Wi-Fi control?", a: "Many modern systems provide built-in or optional Wi-Fi and smart controls depending on the manufacturer and model." },
  { q: "Are VEU incentives available?", a: "Selected approved high-efficiency reverse-cycle systems may qualify depending on existing equipment, replacement configuration, product eligibility and current VEU requirements." },
];

export default function Ducted() {
  return (
    <>
      <SEO
        title="Ducted Air Conditioning Melbourne | Airconditioning Victoria"
        description="Whole-home ducted reverse-cycle air conditioning installation across Melbourne and Victoria. Talk to Airconditioning Victoria about a tailored ducted heating and cooling solution."
        path="/ducted-air-conditioning"
      />
      <PageHero
        eyebrow="Ducted Reverse-Cycle Air Conditioning"
        titleLines={["Whole-home comfort,", <GradientText key="g">beautifully concealed.</GradientText>]}
        intro="Ducted refrigerated air conditioning is one of the most complete ways to heat and cool an entire home. Conditioned air is distributed through concealed ductwork, providing comfortable heating and cooling without multiple wall-mounted indoor units throughout the property."
        ctaText="Get a Free Ducted Quote"
        image="/assets/ducted-hero.webp"
        imageAlt="Melbourne living room with discreet linear ducted air conditioning outlets"
      />

      {/* WHAT IS */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="ducted-what-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-3xl">
            <Eyebrow>The System</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              What is ducted refrigerated air conditioning?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mutedblue">
              A ducted system generally consists of an outdoor condenser, an indoor fan-coil unit
              and insulated ductwork distributing conditioned air to outlets throughout the
              property.
            </p>
            <p className="mt-4 text-base leading-relaxed text-mutedblue">
              Reverse-cycle technology allows the same system to provide refrigerated cooling during
              summer and heating during colder Victorian weather. Depending on the equipment and
              controller selected, the property may also be divided into zones so unused areas can
              be switched off independently.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHAT_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="h-full rounded-[18px] border border-cardline bg-white p-7 shadow-[0_8px_32px_rgba(5,59,117,0.04)] transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-ice">
                    <c.icon className="h-5 w-5 text-brand-blue" />
                  </span>
                  <h3 className="mt-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-navy">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mutedblue">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-white py-24 md:py-32" data-testid="ducted-benefits-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-2xl">
            <Eyebrow>Benefits</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Why choose <GradientText>ducted air conditioning?</GradientText>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}>
                <div className="h-full rounded-[18px] border border-cardline bg-white p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan hover:shadow-[0_16px_44px_rgba(5,59,117,0.08)]">
                  <h3 className="font-display text-sm font-bold uppercase tracking-[0.15em] text-navy">{b.title}</h3>
                  <div className="mt-3 h-0.5 w-10 bg-gradient-brand" aria-hidden="true" />
                  <p className="mt-3 text-sm leading-relaxed text-mutedblue">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DIAGRAM */}
      <section className="bg-cloud py-24 md:py-32" data-testid="ducted-diagram-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="mx-auto max-w-2xl text-center">
            <Eyebrow className="justify-center">How It Flows</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              One system. Every room.
            </h2>
          </Reveal>
          <div className="mt-14 flex flex-col items-stretch gap-4 lg:flex-row lg:items-center">
            {DIAGRAM.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.1} className="flex-1">
                <div className="flex items-center gap-4 lg:block">
                  <div className="flex-1 rounded-[18px] border border-cardline bg-white p-6 text-left shadow-[0_8px_32px_rgba(5,59,117,0.04)] lg:text-center">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-brand">
                      <d.icon className="h-6 w-6 text-white" />
                    </span>
                    <h3 className="mt-3 font-display text-xs font-bold uppercase tracking-[0.18em] text-navy">{d.title}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-mutedblue">{d.text}</p>
                  </div>
                  {i < DIAGRAM.length - 1 && (
                    <ArrowRight className="h-5 w-5 shrink-0 rotate-90 text-brand-cyan lg:mx-auto lg:my-3 lg:rotate-0" aria-hidden="true" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IS IT RIGHT */}
      <section className="bg-white py-24 md:py-32" data-testid="ducted-fit-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Suitability</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Is ducted air conditioning right for your home?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mutedblue">
              Ducted air conditioning can be particularly suitable where you want to heat and cool
              much of the home, prefer a clean interior appearance and have suitable ceiling or
              under-floor space for the indoor unit and ductwork.
            </p>
            <p className="mt-4 text-base leading-relaxed text-mutedblue">
              Our team can assess the property and discuss available options before recommending
              equipment.
            </p>
            <div className="mt-8">
              <PrimaryButton to="/contact" testid="ducted-fit-cta">Request a Ducted Quote</PrimaryButton>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="space-y-4 rounded-[20px] border border-cardline bg-offwhite p-8">
              {[
                "You want to condition most or all of the home",
                "You prefer no visible wall-mounted indoor units",
                "You have ceiling or under-floor space available",
                "You want optional zoning for different areas",
                "You value one system for heating and cooling",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm font-medium text-ink">
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-brand">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="ducted-faq-section">
        <div className="mx-auto max-w-4xl px-5">
          <Reveal className="text-center">
            <Eyebrow className="justify-center">Common Questions</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Ducted air conditioning FAQs.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <FAQ items={FAQS} testidPrefix="ducted-faq" />
          </Reveal>
          <Reveal delay={0.15} className="mt-10 text-center">
            <PrimaryButton to="/contact" testid="ducted-faq-cta">Request a Ducted Quote</PrimaryButton>
          </Reveal>
        </div>
      </section>

      <FinalCTA
        heading="Considering ducted air conditioning?"
        text="Talk to our team about a tailored ducted reverse-cycle solution for your Melbourne or Victorian property."
      />
    </>
  );
}
